import { useEffect, useState } from "react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SmartImage } from "@/components/SmartImage";
import { Reveal } from "@/components/Reveal";
import { OrderDialog } from "@/components/OrderDialog";
import { fetchProducts, formatPHP } from "@/lib/api";
import { CATEGORY_BADGE, CATEGORY_LABEL } from "@/data/site";
import { Check, FlaskConical, ShoppingBag, ArrowRight, TicketPercent } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { getScienceIcon, slugForScience } from "@/data/science";

export const ProductMenu = ({ content = {}, payment = {}, disclaimer }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderFor, setOrderFor] = useState(null);

  useEffect(() => {
    let mounted = true;
    fetchProducts()
      .then((d) => mounted && setProducts(d))
      .catch(() => mounted && setProducts([]))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section id="menu" className="scroll-anchor bg-secondary/30">
      <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <Reveal>
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-wider text-accent">{content.eyebrow}</p>
            <h2 className="font-display mt-2 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              {content.heading}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">{content.body}</p>
            <Link
              to="/science"
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
              data-testid="product-menu-science-index-link"
            >
              <FlaskConical className="h-4 w-4" /> Alamin ang buong science ng Ecentra <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>

        <div className="mt-8">
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-24 w-full rounded-xl" />
              ))}
            </div>
          ) : (
            <Accordion type="single" collapsible className="space-y-3" data-testid="product-menu-accordion">
              {products.map((p) => (
                <AccordionItem
                  key={p.slug || p.id}
                  value={p.slug || p.id}
                  className="overflow-hidden rounded-xl border border-border bg-card"
                  data-testid={`menu-item-${p.slug}`}
                >
                  <AccordionTrigger className="px-4 py-3 hover:no-underline" data-testid={`menu-trigger-${p.slug}`}>
                    <div className="flex flex-1 items-center gap-4 pr-3 text-left">
                      <div className="h-14 w-24 shrink-0 overflow-hidden rounded-lg bg-gradient-to-br from-secondary/60 to-muted">
                        <SmartImage src={p.imageUrl} alt={p.name} fallbackLabel={p.line} fit="contain" className="p-1" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium", CATEGORY_BADGE[p.category] || CATEGORY_BADGE.apparel)}>
                          {CATEGORY_LABEL[p.category] || p.category}
                        </span>
                        <h3 className="mt-1 truncate text-base font-semibold text-foreground">{p.name}</h3>
                        <p className="truncate text-xs text-muted-foreground">{p.tagline}</p>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <span className="font-display text-base font-semibold text-foreground">{formatPHP(p.pricePHP)}</span>
                        <span className="inline-flex items-center gap-1 rounded-full border border-accent/30 bg-accent/12 px-1.5 py-0.5 text-[10px] font-semibold text-accent">
                          <TicketPercent className="h-2.5 w-2.5" /> Promo
                        </span>
                      </div>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent className="px-4 pb-5">
                    <p className="text-sm leading-relaxed text-foreground/80">{p.description}</p>

                    <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
                      {(p.features || []).length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Features</h4>
                          <ul className="mt-2 space-y-1.5">
                            {p.features.map((f) => (
                              <li key={f} className="flex items-start gap-2 text-sm text-foreground/80">
                                <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                                <span>{f}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {(p.benefits || []).length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Advantages</h4>
                          <ul className="mt-2 space-y-1.5">
                            {p.benefits.map((bn) => (
                              <li key={bn} className="flex items-start gap-2 text-sm text-foreground/80">
                                <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                                <span>{bn}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {(p.variants || []).length > 0 && (
                      <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
                        {p.variants.map((g) => (
                          <div key={g.name}>
                            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{g.name}</p>
                            <p className="mt-1 text-sm text-foreground/80">{(g.options || []).join(" \u2022 ")}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {(p.science || []).length > 0 && (
                      <div className="mt-6 rounded-xl border border-border bg-secondary/50 p-4" data-testid={`menu-science-${p.slug}`}>
                        <div className="flex items-center gap-2">
                          <FlaskConical className="h-4 w-4 text-primary" />
                          <h4 className="text-sm font-semibold text-foreground">Ang Science sa Likod ng Infused Elements</h4>
                        </div>
                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                          {p.science.map((s) => {
                            const SciIcon = getScienceIcon(s.title);
                            const sciSlug = slugForScience(s.title);
                            return (
                              <div key={s.title} className="flex gap-3">
                                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/12 text-primary">
                                  <SciIcon className="h-4 w-4" />
                                </span>
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-foreground">{s.title}</p>
                                  <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{s.desc}</p>
                                  {sciSlug ? (
                                    <Link
                                      to={`/science/${sciSlug}`}
                                      className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                                      data-testid={`menu-science-link-${sciSlug}`}
                                    >
                                      Alamin pa <ArrowRight className="h-3 w-3" />
                                    </Link>
                                  ) : null}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="mt-5">
                      <Button
                        className="bg-accent text-accent-foreground hover:bg-accent/90"
                        onClick={() => setOrderFor(p)}
                        data-testid={`menu-order-${p.slug}`}
                      >
                        <ShoppingBag className="mr-2 h-4 w-4" />
                        {payment.gcashEnabled ? "Buy" : "I-order / Mag-inquire"}
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </div>

      <OrderDialog product={orderFor} open={Boolean(orderFor)} onClose={() => setOrderFor(null)} payment={payment} disclaimer={disclaimer} />
    </section>
  );
};
